'use client';
import React, { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { Users, Copy, Check, Gift, TrendingUp, Globe, RefreshCw, Link2 } from 'lucide-react';
import { useWalletAuth } from '@/contexts/WalletAuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface ReferralRecord {
  id: string;
  referred_email: string;
  referred_name: string;
  referred_country: string;
  is_active: boolean;
  email_verified: boolean;
  telegram_joined: boolean;
  reward_amount: number;
  reward_paid: boolean;
  created_at: string;
  activated_at: string;
}

interface UserProfile {
  referral_code: string;
  full_name: string;
  email: string;
}

export default function DaramReferralPage() {
  const { walletUser, supabaseUser } = useWalletAuth();
  const user = supabaseUser || (walletUser ? { id: walletUser.id } : null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [referrals, setReferrals] = useState<ReferralRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [rewardPerReferral, setRewardPerReferral] = useState(100);
  const [platformTelegram, setPlatformTelegram] = useState('');
  const [siteUrl, setSiteUrl] = useState('https://aidaram.net');

  useEffect(() => {
    setSiteUrl(process.env.NEXT_PUBLIC_SITE_URL || 'https://aidaram.net');
  }, []);

  useEffect(() => {
    const userId = walletUser?.id || supabaseUser?.id;
    if (!userId) return;
    const fetchData = async () => {
      const supabase = createClient();
      const [profileRes, referralsRes, settingsRes] = await Promise.all([
        supabase.from('user_profiles').select('referral_code, full_name, email').eq('id', userId).single(),
        supabase.from('referrals').select('*').eq('referrer_id', userId).order('created_at', { ascending: false }),
        supabase.from('admin_settings').select('key, value').in('key', ['referral_reward_amount', 'platform_telegram_link']),
      ]);
      if (profileRes.data) setProfile(profileRes.data);
      setReferrals(referralsRes.data || []);
      if (settingsRes.data) {
        const rewardSetting = settingsRes.data.find(s => s.key === 'referral_reward_amount');
        const telegramSetting = settingsRes.data.find(s => s.key === 'platform_telegram_link');
        if (rewardSetting?.value) setRewardPerReferral(parseInt(rewardSetting.value));
        if (telegramSetting?.value) setPlatformTelegram(telegramSetting.value);
      }
      setLoading(false);
    };
    fetchData();
  }, [walletUser?.id, supabaseUser?.id]);

  const referralLink = profile?.referral_code
    ? `${siteUrl}/sign-up-login?ref=${profile.referral_code}`
    : '';

  const copyLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const activeReferrals = referrals.filter(r => r.is_active);
  const pendingReferrals = referrals.filter(r => !r.is_active);
  const totalEarned = activeReferrals.reduce((sum, r) => sum + (r.reward_amount || rewardPerReferral), 0);

  if (!user && !walletUser) {
    return (
      <div className="min-h-screen bg-[#07111F]">
        <Header />
        <div className="pt-32 pb-16 text-center">
          <p className="text-[#93A4B8]">Please <a href="/sign-up-login" className="text-[#16C2FF] hover:underline">sign in</a> to access your referral dashboard.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07111F]">
      <Header />
      <main className="pt-24 pb-16 px-4 max-w-screen-xl mx-auto">
        {/* Hero */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[rgba(22,194,255,0.1)] border border-[rgba(22,194,255,0.25)] mb-4">
            <Gift size={13} className="text-[#16C2FF]" />
            <span className="text-xs font-sora font-600 text-[#16C2FF]">DARAM Referral Program</span>
          </div>
          <h1 className="font-sora font-800 text-3xl sm:text-4xl text-[#E6F1FF] mb-3">
            Earn DARAM by Referring Friends
          </h1>
          <p className="text-[#93A4B8] font-dm text-lg max-w-2xl">
            Share your referral link. When your friend signs up, verifies their email, and joins the AIDARAM Telegram, you earn <span className="text-[#16C2FF] font-medium">{rewardPerReferral} DARAM</span> per active referral.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Referrals', value: referrals.length, color: 'text-[#E6F1FF]', icon: Users },
            { label: 'Active Referrals', value: activeReferrals.length, color: 'text-[#00E5A8]', icon: Check },
            { label: 'Pending', value: pendingReferrals.length, color: 'text-[#F59E0B]', icon: RefreshCw },
            { label: 'DARAM Earned', value: `${totalEarned.toLocaleString()}`, color: 'text-[#16C2FF]', icon: TrendingUp },
          ].map(stat => {
            const StatIcon = stat.icon;
            return (
              <div key={stat.label} className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.1)] rounded-xl p-5">
                <StatIcon size={16} className={`${stat.color} mb-2`} />
                <p className={`text-2xl font-sora font-bold ${stat.color}`}>{stat.value}</p>
                <p className="text-xs text-[#4A5568] mt-0.5">{stat.label}</p>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Referral Link Card — full width on mobile, 2/3 on desktop */}
          <div className="lg:col-span-2 bg-[#0D1B2A] border border-[rgba(22,194,255,0.15)] rounded-2xl p-6">
            <h2 className="font-sora font-700 text-lg text-[#E6F1FF] mb-2">Your Referral Link</h2>
            <p className="text-sm text-[#93A4B8] font-dm mb-5">Share this link with friends. When they sign up and become active, you earn DARAM.</p>

            {loading ? (
              <div className="h-12 bg-[rgba(255,255,255,0.04)] rounded-lg animate-pulse" />
            ) : (
              <>
                {/* Referral link display — always visible */}
                <div className="mb-3 p-3 bg-[rgba(22,194,255,0.04)] border border-[rgba(22,194,255,0.15)] rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 size={13} className="text-[#16C2FF] flex-shrink-0" />
                    <span className="text-xs font-semibold text-[#16C2FF] uppercase tracking-wider">Your Referral Link</span>
                  </div>
                  <p className="text-sm text-[#E6F1FF] font-mono break-all leading-relaxed">
                    {referralLink || 'Loading...'}
                  </p>
                </div>

                {/* Copy button — always visible, full width on mobile */}
                <button
                  onClick={copyLink}
                  disabled={!referralLink}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#16C2FF] text-[#07111F] rounded-xl text-sm font-semibold hover:bg-[#0EA5E9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {copied ? <><Check size={15} /> Copied to Clipboard!</> : <><Copy size={15} /> Copy Referral Link</>}
                </button>
              </>
            )}

            <div className="mt-5 bg-[rgba(22,194,255,0.05)] border border-[rgba(22,194,255,0.1)] rounded-xl p-4">
              <h4 className="text-xs font-semibold text-[#16C2FF] mb-2 uppercase tracking-wider">How Activation Works</h4>
              <div className="space-y-2">
                {[
                  { step: '1', text: 'Friend signs up using your referral link' },
                  { step: '2', text: 'Friend verifies their email address' },
                  { step: '3', text: 'Friend joins the AIDARAM Telegram channel' },
                  { step: '4', text: `You receive ${rewardPerReferral} DARAM automatically` },
                ].map(s => (
                  <div key={s.step} className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-[rgba(22,194,255,0.15)] text-[#16C2FF] text-xs font-bold flex items-center justify-center flex-shrink-0">{s.step}</span>
                    <span className="text-xs text-[#93A4B8] font-dm">{s.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {platformTelegram && (
              <a
                href={platformTelegram}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 flex items-center gap-2 text-sm text-[#16C2FF] hover:underline"
              >
                <Globe size={14} />
                Join AIDARAM Telegram (share with your referrals)
              </a>
            )}
          </div>

          {/* Reward Info */}
          <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.1)] rounded-2xl p-6">
            <h2 className="font-sora font-700 text-lg text-[#E6F1FF] mb-4">Reward Details</h2>
            <div className="space-y-4">
              <div className="bg-[rgba(22,194,255,0.06)] border border-[rgba(22,194,255,0.15)] rounded-xl p-4 text-center">
                <p className="text-xs text-[#93A4B8] mb-1">Per Active Referral</p>
                <p className="text-3xl font-sora font-800 text-[#16C2FF]">{rewardPerReferral}</p>
                <p className="text-sm text-[#93A4B8]">DARAM</p>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#93A4B8]">Active referrals</span>
                  <span className="text-[#00E5A8] font-medium">{activeReferrals.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#93A4B8]">Total earned</span>
                  <span className="text-[#16C2FF] font-medium">{totalEarned.toLocaleString()} DARAM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#93A4B8]">Pending activation</span>
                  <span className="text-[#F59E0B] font-medium">{pendingReferrals.length}</span>
                </div>
              </div>
              <p className="text-xs text-[#4A5568] font-dm leading-relaxed">
                Rewards are credited to your DARAM balance once a referral becomes active (email verified + Telegram joined).
              </p>
            </div>
          </div>
        </div>

        {/* Referral List */}
        <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-[rgba(22,194,255,0.08)] flex items-center justify-between">
            <h3 className="font-sora font-600 text-sm text-[#E6F1FF]">Your Referrals</h3>
            <span className="text-xs text-[#93A4B8]">{referrals.length} total</span>
          </div>
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-[#16C2FF] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : referrals.length === 0 ? (
            <div className="p-12 text-center">
              <Users size={36} className="text-[#93A4B8]/30 mx-auto mb-3" />
              <p className="text-sm text-[#93A4B8]">No referrals yet. Share your link to get started!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[rgba(22,194,255,0.06)]">
                    <th className="text-left text-xs font-semibold text-[#4A5568] uppercase tracking-wider px-5 py-3">Name / Email</th>
                    <th className="text-left text-xs font-semibold text-[#4A5568] uppercase tracking-wider px-5 py-3">Country</th>
                    <th className="text-left text-xs font-semibold text-[#4A5568] uppercase tracking-wider px-5 py-3">Email Verified</th>
                    <th className="text-left text-xs font-semibold text-[#4A5568] uppercase tracking-wider px-5 py-3">Telegram</th>
                    <th className="text-left text-xs font-semibold text-[#4A5568] uppercase tracking-wider px-5 py-3">Status</th>
                    <th className="text-left text-xs font-semibold text-[#4A5568] uppercase tracking-wider px-5 py-3">Reward</th>
                    <th className="text-left text-xs font-semibold text-[#4A5568] uppercase tracking-wider px-5 py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map(r => (
                    <tr key={r.id} className="border-b border-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.02)] transition-colors">
                      <td className="px-5 py-3">
                        <div>
                          <p className="text-sm text-[#E6F1FF]">{r.referred_name || '—'}</p>
                          <p className="text-xs text-[#93A4B8]">{r.referred_email || '—'}</p>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-xs text-[#93A4B8]">{r.referred_country || '—'}</span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-medium ${r.email_verified ? 'text-[#00E5A8]' : 'text-[#93A4B8]'}`}>
                          {r.email_verified ? '✓ Yes' : '✗ No'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-medium ${r.telegram_joined ? 'text-[#00E5A8]' : 'text-[#93A4B8]'}`}>
                          {r.telegram_joined ? '✓ Joined' : '✗ Pending'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${r.is_active ? 'bg-[rgba(0,229,168,0.15)] text-[#00E5A8]' : 'bg-[rgba(245,158,11,0.15)] text-[#F59E0B]'}`}>
                          {r.is_active ? 'Active' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-medium ${r.reward_paid ? 'text-[#00E5A8]' : 'text-[#93A4B8]'}`}>
                          {r.reward_paid ? `${r.reward_amount || rewardPerReferral} DARAM ✓` : 'Pending'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-xs text-[#93A4B8]">{new Date(r.created_at).toLocaleDateString()}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
